/**
 * OnboardingTour - Guide new users through the platform
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Sparkles, Zap, Image, MessageSquare, Gift, 
  ArrowRight, X, CheckCircle
} from 'lucide-react';

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to TripleTC!',
    description: 'Your AI-powered marketplace for getting things done. Let us show you around!',
    icon: Sparkles,
    color: 'teal'
  },
  {
    id: 'free-tasks',
    title: '5 Free Tasks',
    description: 'You have 5 free tasks to try any AI capability. Content, images, videos - all free!',
    icon: Gift,
    color: 'green'
  },
  {
    id: 'templates',
    title: 'Quick Task Templates',
    description: 'Choose from ready-made templates or describe any task with Custom Magic.',
    icon: Zap,
    color: 'yellow'
  },
  {
    id: 'media',
    title: 'Create Images & Videos',
    description: 'Generate stunning images and videos with AI. Perfect for content and marketing.',
    icon: Image,
    color: 'pink'
  },
  {
    id: 'chatbots',
    title: 'Build AI Chatbots',
    description: 'Upload your documents and create custom chatbots for your website or business.',
    icon: MessageSquare,
    color: 'purple'
  }
];

export default function OnboardingTour({ onComplete }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has seen the tour
    const hasSeenTour = localStorage.getItem('tripletc_tour_completed');
    if (!hasSeenTour && user) {
      setIsVisible(true);
    }
  }, [user]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const handleSkip = () => {
    completeTour();
  };

  const completeTour = () => {
    localStorage.setItem('tripletc_tour_completed', 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleGetStarted = () => {
    completeTour();
    navigate('/templates');
  };

  if (!isVisible) return null;

  const step = TOUR_STEPS[currentStep];
  const StepIcon = step.icon;

  const colorMap = {
    teal: { bg: 'bg-teal-500/20', text: 'text-teal-400', border: 'border-teal-500/30' },
    green: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
    yellow: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
    pink: { bg: 'bg-pink-500/20', text: 'text-pink-400', border: 'border-pink-500/30' },
    purple: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
  };

  const colors = colorMap[step.color];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6"
      >
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          className={`bg-zinc-900 border ${colors.border} rounded-2xl p-8 max-w-md w-full relative`}
        >
          {/* Close Button */}
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Step Indicator */}
          <div className="flex gap-2 mb-6">
            {TOUR_STEPS.map((_, index) => (
              <div
                key={index}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  index <= currentStep ? 'bg-teal-500' : 'bg-zinc-700'
                }`}
              />
            ))}
          </div>

          {/* Icon */}
          <div className={`w-16 h-16 ${colors.bg} rounded-2xl flex items-center justify-center mb-6`}>
            <StepIcon className={`w-8 h-8 ${colors.text}`} />
          </div>

          {/* Content */}
          <h2 className="text-2xl font-bold text-white mb-3">{step.title}</h2>
          <p className="text-zinc-400 text-lg mb-8">{step.description}</p>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleSkip}
              className="text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              Skip tour
            </button>

            {currentStep < TOUR_STEPS.length - 1 ? (
              <Button onClick={handleNext} className="bg-teal-500 hover:bg-teal-600">
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleGetStarted} className="bg-gradient-to-r from-teal-500 to-blue-500">
                <Sparkles className="w-4 h-4 mr-2" />
                Start Creating
              </Button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Hook to trigger tour
export function useTriggerTour() {
  const resetTour = () => {
    localStorage.removeItem('tripletc_tour_completed');
    window.location.reload();
  };

  return { resetTour };
}
